import { useState, useEffect, useRef } from "react";
import { CSSTransition } from 'react-transition-group'; 


import { useStepContext } from "../../utils/StepContext.jsx"
import { useFormContext } from "../../utils/FormContext.jsx"
import { useMediaQuery } from 'react-responsive';
import { useAuth } from '../../utils/AuthContext/AuthContext.jsx'

import MainCard from "../MainCard.jsx";
import ProgressBar from "../ProgressBar/ProgressBar.jsx";
import SaveButton from "../SaveButton.jsx";
import PDFPreview from "../PDFPreview.jsx";
import HelpButton from "../HelpButton.jsx";
import ShishitoAssist from "../ShishitoAssist/ShishitoAssist.jsx";
import InfoSVG from "../../assets/InfoSVG.jsx";

const PriceTerms = () => {

    const { handleNext, handleBack } = useStepContext();
    const { formData, formId, handlePriceInputChange, handleRadioChange, handleTenantTypeChange, handleIsLoanChange, handleUncommonPurchaseSituationsChange, propertyData, handleEarnestMoneyPayableChange, handleLoanTypeChange, handleLoanTypeOtherInputChange} = useFormContext(); 
    const [ selectedOption, setSelectedOption ] = useState("");
    const [autoSetOther, setAutoSetOther] = useState(false);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const { token, user } = useAuth(); // Use user and token from context
    const [isAssistVisible, setIsAssistVisible] = useState(false);

    const currentPageValue = 1;
    const currentSubStepProgress = 2/3;

    const [errors, setErrors] = useState({
        purchasePrice: false, 
        earnestMoney: false
    }); 

    const purchasePriceRef = useRef(null); 
    const earnestMoneyRef = useRef(null); 
    

    // Validate inputs
    const validateFields = () => {
        const newErrors = {};
        newErrors.purchasePrice = !purchasePriceRef.current?.value;  // true if empty
        newErrors.earnestMoney = !earnestMoneyRef.current?.value;  // true if empty
        setErrors(newErrors);

        // Proceed only if all fields are filled
        return !newErrors.purchasePrice && !newErrors.earnestMoney;
    };

    const onNext = () => {
        handleNext();
    };

    const toggleAssist = () => {
        setIsAssistVisible(!isAssistVisible);
    };

    let listPrice = propertyData?.propertyDetails?.price;
    let formattedPrice = listPrice 
    ? Math.round(listPrice).toLocaleString('en-US', { style: 'currency', currency: 'USD' }).split('.')[0]
    : "Price not available";
    let buyersAgentCommission2 = (listPrice * .02)
    ? Math.round(listPrice * .02).toLocaleString('en-US', { style: 'currency', currency: 'USD' }).split('.')[0]
    : "Commission not available";
    let buyersAgentCommission3 = (listPrice * 0.03)
    ? Math.round(listPrice * .03).toLocaleString('en-US', { style: 'currency', currency: 'USD' }).split('.')[0]
    : "Commission not available";

    const handleUncommonSituationChange = (situation) => {
        handleUncommonPurchaseSituationsChange(formData.uncommonPurchaseSituations === situation ? '' : situation);

        if (situation === 'assumeMortgage' || situation === 'privateFinancing' || situation === 'sellerFinancing') {
            handlePriceInputChange('cashAtClosing', '0'); 
        }
    };

    // Jumbo Loan Function
    useEffect(() => {
        const newLoanAmountNumber = parseFloat(formData.prices[0].newLoanAmount.replace(/,/g, ''));
        if (newLoanAmountNumber >= 806500) {
            if(!formData.loanType.other) {
            // Set 'Other' loan type to true and specify 'Jumbo'
            handleLoanTypeChange('other', true);
            handleLoanTypeOtherInputChange({
                target: { name: 'loanTypeOtherInput', value: 'Jumbo' }
            });
            setAutoSetOther(true); 
            }
        } else if (autoSetOther) {
            // Ensure 'Other' is not selected if below threshold
                handleLoanTypeChange('other', false);
                handleLoanTypeOtherInputChange({
                    target: { name: 'loanTypeOtherInput', value: '' }
                });
                setAutoSetOther(false); 
            }
    }, [formData.prices[0].newLoanAmount, formData.loanType.other]);

    const handleOtherChange = (event) => {
        const isChecked = event.target.checked;
        handleLoanTypeChange('other', isChecked);
        if (!isChecked) {
            handleLoanTypeOtherInputChange({
                target: { name: 'loanTypeOtherInput', value: '' }
            });
            setAutoSetOther(false); // Ensure auto-set is disabled when manually unchecked
        }
    };

    const formatValue = (inputValue) => {
        // Remove any non-digit characters except periods
        const numericValue = inputValue.replace(/[^\d.]/g, '');
        
        // Split the value into whole and decimal parts
        const [whole, decimal] = numericValue.split('.');
        
        // Format the whole part with commas
        const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        
        // Concatenate the whole and decimal parts with a decimal point
        let formattedValue = formattedWhole;
        if (decimal !== undefined) {
            formattedValue += '.' + decimal;
        }  
        return formattedValue;
    };

    const calculateCashAtClosing = () => {
        const purchasePrice = parseFloat(formData.prices[0].purchasePrice.replace(/,/g, '')) || 0;
        const earnestMoney = parseFloat(formData.prices[0].earnestMoney.replace(/,/g, '')) || 0;
        
        // Check if any input is NaN (not a number)
        if (isNaN(purchasePrice)) {
            return ''; 
        }

        let cashAtClosing; 

        // Check if isLoan is false or not selected
        if (formData.uncommonPurchaseSituations === 'assumeMortgage' && !formData.isLoan) {
            cashAtClosing = 0
            parseFloat(formData.prices[0].cashAtClosing.replace(/,/g, ''));
        }
        else if (formData.isLoan) {
            const newLoanAmount = parseFloat(formData.prices[0].newLoanAmount.replace(/,/g, '')) || 0;
            cashAtClosing = purchasePrice - earnestMoney - newLoanAmount;

        } else {
            // If isLoan is true, include the new loan amount in the calculation
            cashAtClosing = purchasePrice - earnestMoney;
        }
        
        // Round down and calculate
        const cashAtClosingWhole = Math.floor(cashAtClosing);
        return cashAtClosingWhole.toLocaleString();
    };
    
    const handleLoanPurchaseChange = (value) => {
        setSelectedOption(value); 
        handleIsLoanChange(value);  

        if (value === 'yes') {
            handleUncommonSituationChange('');
            calculateCashAtClosing();        
        
         } else if (value === 'no') {
            handlePriceInputChange('newLoanAmount', '0');
            handlePriceInputChange('percentageDown', '0');
            // Set each loan type checkbox to false
            handleLoanTypeChange('other', false);
            handleLoanTypeChange('conventional', false);
            handleLoanTypeChange('FHA', false);
            handleLoanTypeChange('VA', false);
            handleLoanTypeChange('bond', false);
            
            // Clear the input field
            handleLoanTypeOtherInputChange({ target: { value: '' } });
            calculateCashAtClosing();
        }
    }; 

    const handleAutoPopulateCashAtClosing = () => {
        const cashAtClosing = calculateCashAtClosing();
        handlePriceInputChange('cashAtClosing', cashAtClosing);

        // After setting cashAtClosing, calculate total price and update it
        const updatedPrices = { ...formData.prices[0], cashAtClosing };
        const totalPrice = calculateTotalPrice(updatedPrices);
        handlePriceInputChange('totalPrice', totalPrice.toLocaleString());
    };

    const handleInputChange = (field, e) => {
        const value = formatValue(e.target.value);
        handlePriceInputChange(field, value);

        // After updating a field, recalculate the total price
        const updatedPrices = { ...formData.prices[0], [field]: value.replace(/,/g, '') };
        const totalPrice = calculateTotalPrice(updatedPrices);
        handlePriceInputChange('totalPrice', totalPrice.toLocaleString());
    }; 

    const calculateTotalPrice = (prices) => {
        const { earnestMoney, newLoanAmount, assumptionBalance, privateFinancing, sellerFinancing, cashAtClosing } = prices;
        return [earnestMoney, newLoanAmount, assumptionBalance, privateFinancing, sellerFinancing, cashAtClosing]
            .map(val => parseFloat(val.replace(/,/g, '')) || 0) // Remove commas and parse
            .reduce((acc, val) => acc + val, 0);
    };
      
    useEffect(() => {
        const purchasePrice = parseFloat(formData.prices[0].purchasePrice.replace(/,/g, '')) || 0;
        const percentageDown = parseFloat(formData.prices[0].percentageDown) / 100 || 0;
        const newLoanAmount = purchasePrice * (1 - percentageDown);
    
        // Update the newLoanAmount in the state
        handlePriceInputChange('newLoanAmount', newLoanAmount.toLocaleString());
    }, [formData.prices[0].purchasePrice, formData.prices[0].percentageDown])
    
    useEffect(() => {
        handleAutoPopulateCashAtClosing();
    }, [formData.prices[0].purchasePrice, formData.prices[0].earnestMoney, formData.prices[0].newLoanAmount]);

    // console.log(formData);
    // console.log(formData.isLoan);

    return (
        <div className="w-full flex flex-col items-center">
        {/* <ProgressBar currentPage={currentPageValue} subStepProgress={currentSubStepProgress}/> */}
        <MainCard width={750}>
        <div className="overflow-y-auto md:max-h-[425px] max-h-[45vh] w-full">
            <div className="inline-flex w-full justify-between mb-5">
            {isMobile ? <h3 className="text-black text-[18px] text-left font-normal font-['Inter']">Price & terms</h3> : <h3 className="text-black text-xl text-left font-normal font-['Inter']">Price & terms</h3>}
                <div className='gap-2 md:gap-4 items-center inline-flex'>
                    <HelpButton onClick={toggleAssist}/>
                    <SaveButton formData={formData} formId={formId}/>
                    {user && <PDFPreview formData={formData}/>}
                </div>
            </div>
            {/*TOP SECTION TOGGLES */}           
            <div className="flex flex-col gap-2 w-full text-left">
                    <div className="border-blue-500 bg-blue-50 border rounded-md md:p-2 p-1 inline-flex justify-between w-full gap-2 mb-4 items-start">
                    {isMobile ? <InfoSVG width={18} height={18} fill='#2563EB'/> : <InfoSVG width={23} height={23} fill='#2563EB'/>}
                        <span className="text-sm text-blue-800">
                            Typical buyer's agent commissions <b>(2-3% or {buyersAgentCommission2} - {buyersAgentCommission3} at current list price)</b> won't apply since you're self-representing. Consider this when making your offer.
                        </span>
                </div>
                <div className="w-full inline-flex justify-between md:pr-6 items-center">
                    <span className="text-sm text-slate-600 w-auto pl-2 font-['Inter']">Will you be purchasing with a loan?</span>
                </div>
                <div className="self-stretch justify-start gap-[25px] inline-flex items-center pl-2 mb-2">
                    <div className="items-center gap-2 flex">
                            <input 
                                type="radio" 
                                id="yesLoanPurchase"
                                value="yes" 
                                checked={formData.isLoan === true} 
                                onChange={() => handleLoanPurchaseChange("yes")}
                            />
                            <label className="text-black font-['Inter']">Yes</label>
                        </div> 
                    <div className="items-center gap-2 flex">
                            <input 
                                type="radio" 
                                id="noLoanPurchase"
                                value="no" 
                                checked={formData.isLoan === false} 
                                onChange={() => handleLoanPurchaseChange("no")}
                            />
                            <label className="text-black font-['Inter']">No</label>
                    </div> 
                </div>
            </div>

            {/* PURCHASING TABLE & LOAN TYPE*/}

            {formData.isLoan ? (
    <div className="flex flex-col text-left justify-start items-start w-full gap-2 pl-2">
        <div className="w-full flex flex-col">
            <span className="text-sm text-slate-600 font-['Inter'] mt-4 mb-1">Loan Type</span>

            <div className="self-stretch flex-wrap md:flex-nowrap justify-start gap-[25px] inline-flex text-[13px] md:text-[16px] items-center w-full">
                <div className="items-center gap-2 flex">
                    <input 
                        type="checkbox"
                        id="conventional" 
                        value="conventional"
                        checked={formData.loanType['conventional']} 
                        onChange={(e) => handleLoanTypeChange('conventional', e.target.checked)}
                    />
                    <label className="text-black text-opacity-60 font-semibold font-['Inter']">Conventional</label>
                </div>
                <div className="items-center gap-2 flex">
                    <input 
                        type="checkbox" 
                        id="FHA"
                        value="FHA"
                        checked={formData.loanType['FHA']} 
                        onChange={(e) => handleLoanTypeChange('FHA', e.target.checked)}
                    />
                    <label className="text-black text-opacity-60 font-semibold font-['Inter']">FHA</label>
                </div>
                <div className="items-center gap-2 flex">
                    <input 
                        type="checkbox" 
                        id="VA"
                        value="VA"
                        checked={formData.loanType['VA']} 
                        onChange={(e) => handleLoanTypeChange('VA', e.target.checked)}
                    />
                    <label className="text-black text-opacity-60 font-semibold font-['Inter']">VA</label>
                </div>
                <div className="items-center gap-2 flex">
                    <input 
                        type="checkbox" 
                        id="bond"
                        value="bond"
                        checked={formData.loanType['bond']} 
                        onChange={(e) => handleLoanTypeChange('bond', e.target.checked)}
                    />
                    <label className="text-black text-opacity-60 font-semibold font-['Inter']">Bond</label>
                </div>
                <div className="inline-flex justify-between">
                    <div className="items-center gap-2 flex">
                        <input 
                            type="checkbox" 
                            id="other"
                            value="other"
                            checked={formData.loanType['other']} 
                            onChange={handleOtherChange}
                        />
                        <label className="text-black text-opacity-60 font-semibold font-['Inter']">Other</label>
                    </div>
                </div>
                <div className="w-[55vw] md:w-full h-auto md:h-[39px] mr-1 flex items-center bg-white rounded-md border border-slate-300">
                    <input 
                        className="w-[60vw] md:w-full h-full px-2 py-2 text-left text-sm text-slate-700 placeholder-slate-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                        type="text"
                        name="loanTypeOtherInput"
                        placeholder=""
                        value={formData.loanTypeOtherInput}
                        onChange={handleLoanTypeOtherInputChange}
                    />
                </div>
            </div>
        </div>
        
        <div className="flex flex-col w-full justify-between mt-4">
            <div className="inline-flex">
                <div className="w-[40vh] justify-between gap-1 flex flex-col">
                    <span className='text-sm text-slate-600'>
                        Purchase Price - Current List Price: {formattedPrice ? formattedPrice : "Not For Sale"}
                    </span>
                    <div className="w-[40vh] h-auto md:h-[39px] flex items-center bg-white rounded-md border border-slate-300 focus-within:border-blue-500 focus-within:border-2">
                        <div className=" text-slate-500 font-semibold font-['Inter'] pl-2">$</div>
                        <input 
                            className={`${errors.purchasePrice ? 'border-red-500 bg-red-100' : 'border-slate-300'} w-full h-full py-2 pl-1 text-left text-sm text-slate-700 placeholder-slate-400 rounded-md focus:border-transparent focus:outline-none`}
                            value={formData.prices[0].purchasePrice}
                            placeholder="Enter Your Offer *"
                            onChange={(e) => {
                                handleInputChange('purchasePrice', e);
                                handleAutoPopulateCashAtClosing(); 
                            }}
                            required
                            ref={purchasePriceRef}
                        />
                    </div>
                </div>
            </div>
        
            <div className="flex flex-row h-auto mt-4">
                <div className="inline-flex w-full">
                    <div className="w-[40vh] justify-between gap-1 flex flex-col">
                        <span className='text-sm text-slate-600'>
                            Percentage Down on Purchase Price
                        </span>
                        <div className="w-[40vh] h-auto md:h-[39px] flex items-center bg-white rounded-md border border-slate-300 focus-within:border-blue-500 focus-within:border-2">
                            <div className=" text-slate-500 font-semibold font-['Inter'] pl-2">%</div>
                            <input 
                                className={`w-full h-full py-2 pl-1 text-left text-sm text-slate-700 placeholder-slate-400 rounded-md focus:outline-none focus:border-transparent`}
                                value={formData.prices[0].percentageDown}
                                onChange={(e) => {
                                    handleInputChange('percentageDown', e); 
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
) : (
    <div className="flex items-center mt-4">
        <div className="flex-col w-full justify-between align-text-bottom mx-2">
            <div className="inline-flex">
                <div className="w-[40vh] justify-between gap-1 flex flex-col">
                    <span className='text-sm text-slate-600'>
                        Purchase Price - Current List Price: {formattedPrice ? formattedPrice : "Not For Sale"}
                    </span>
                    <div className="w-[40vh] h-auto md:h-[39px] flex items-center bg-white rounded-md border border-slate-300 focus-within:border-blue-500 focus-within:border-2">
                        <div className=" text-slate-500 font-semibold font-['Inter'] pl-2">$</div>
                        <input 
                            className={`${errors.purchasePrice ? 'border-red-500 bg-red-100' : 'border-slate-300'} w-full h-full py-2 pl-1 text-left text-sm text-slate-700 placeholder-slate-400 rounded-md focus:outline-none forcus:border-transparent`}
                            value={formData.prices[0].purchasePrice}
                            placeholder="Enter Your Offer *"
                            onChange={(e) => {
                                handleInputChange('purchasePrice', e);
                                handleAutoPopulateCashAtClosing(); 
                            }}
                            required
                            ref={purchasePriceRef}
                        />
                    </div>
                </div>
            </div>

            <div className="flex h-auto mt-4">
                <div className="inline-flex w-full">
                    <div className="md:w-[40vw] w-[80vw] justify-between gap-1 flex flex-col">
                        <span className='text-sm text-slate-600'>
                            Percentage Down on Purchase Price
                        </span>
                        <div className="w-[40vh] h-auto md:h-[39px] flex items-center bg-white rounded-md border border-slate-300 focus-within:border-blue-500 focus-within:border-2">
                            <div className=" text-slate-500 font-semibold font-['Inter'] pl-2">%</div>
                            <input 
                                className={`w-full h-full py-2 pl-1 text-left text-sm text-slate-700 placeholder-slate-400 rounded-md focus:outline-none focus:border-transparent`}
                                value={formData.prices[0].percentageDown}
                                onChange={(e) => {
                                    handleInputChange('percentageDown', e); 
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
)}

            {/* EARNEST MONEY SECTION */}

            <div className={`flex flex-col w-[40vh]`}>
                <div className="w-[40vh] justify-between gap-1 items-start flex flex-col pl-2 mt-4">
                    <span className='text-sm text-slate-600'>
                        Earnest Money (Deposit)
                        {/* <br>Inquire with Seller's Agent on Required Earnest Money Value</br> */}
                    </span>
                    <span className="text-[11px] text-slate-700 w-[40vh]">
                        This amount is your choice and negotiable with sellers. Inquire with the seller's agent on the earnest money value they are looking for.
                    </span>
                    <div className="w-[40vh] h-auto md:h-[39px] flex items-center bg-white rounded-md border border-slate-300 focus-within:border-blue-500 focus-within:border-2">
                        <div className="pl-2 text-slate-500 font-semibold font-['Inter']">$</div>
                            <input 
                                className={`${errors.purchasePrice ? 'border-red-500 bg-red-100' : 'border-slate-300'} w-full h-full py-2 pl-1 text-left text-sm text-slate-700 placeholder-slate-400 rounded-md focus:outline-none focus:border-transparent`}
                                value={formData.prices[0].earnestMoney}
                                placeholder="Deposit Amount *"
                                onChange={(e) => {
                                    handleInputChange('earnestMoney', e);
                                    handleAutoPopulateCashAtClosing(); 
                                }}
                                required
                                ref={earnestMoneyRef}
                            />
                    </div>
                </div>
                
                {/* EARNEST MONEY PAYABLE*/}

                <div className="w-[80vw] justify-between gap-1 items-start flex flex-col pl-2 mt-4">
                    <span className='text-sm text-slate-600'>
                        Earnest Money Payment Method
                    </span>
                    <div className="w-[40vh] h-auto md:h-[39px] flex items-center bg-white rounded-md border border-slate-300">
                            <input 
                                className={`w-full h-full py-2 pl-1 text-left text-sm text-slate-700 placeholder-slate-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                type="text"
                                name="earnestMoneyPayable"
                                value={formData.earnestMoneyPayable}
                                onChange={handleEarnestMoneyPayableChange}
                            />
                    </div>
                </div>
            </div>

            {/* NEW LOAN AMOUNT SECTION */}
            {formData.isLoan ? (
            <div className="flex flex-row h-auto items-center w-full pl-2 mt-4">
                <div className="w-[50vh] justify-between gap-1 items-start flex flex-col">
                    <span className='text-sm text-slate-600'>
                        New Loan Amount - Amount from Lender, if any
                    </span>
                    <div className="w-[40vh] h-auto md:h-[39px] flex items-center bg-white rounded-md border border-slate-300 focus-within:border-blue-500 focus-within:border-2">
                        <div className="pl-2 text-slate-500 font-semibold font-['Inter']">$</div>
                        <input 
                            className="text-left pl-2 w-full h-full text-slate-700 text-sm focus:border-transparent focus:outline-none"
                            value={formData.prices[0].newLoanAmount}
                            onChange={(e) => {
                                handleInputChange('newLoanAmount', e);
                                handleAutoPopulateCashAtClosing(); 
                            }}
                        />
                    </div>
                </div>
            </div>
            ) : ( '' )}

            <div className="flex flex-col gap-8 w-full h-auto items-center">
                <div className="inline-flex w-full">
                <div className="w-[80vw] justify-between gap-1 items-start flex flex-col pl-2 mt-4">
                    <span className='text-sm text-slate-600'>
                        Cash at Close - Cash Required on Closing Date
                    </span>
                    <span className="text-[11px] text-slate-700 w-[40vh]">
                        <b>Note:</b> This is subject to change based on final loan terms and closing costs.
                    </span>
                    <div className="w-[40vh] h-auto md:h-[39px] flex items-center bg-white rounded-md border border-slate-300 focus-within:border-blue-500 focus-within:border-2">
                    <div className="pl-2 text-slate-500 font-semibold font-['Inter']">$</div>
                    <input 
                        className={`w-full h-full py-2 pl-1 text-left text-sm text-slate-700 placeholder-slate-400 rounded-md focus:outline-none focus:border-transparent`}
                        value={formData.prices[0].cashAtClosing}
                        onChange={(e) => handleInputChange('cashAtClosing', e)}
                    />
                </div>
                </div>
                </div>
            
                <div className="inline-flex w-full items-center">
                    <div className="w-full justify-between gap-1 items-start flex flex-col pl-2">
                        <span className='text-[14px] md:text-[16px] text-slate-600 font-[Inter]'>Do you currently have cash to cover Cash at Close?</span>
                        <div className="self-stretch justify-start gap-[25px] inline-flex items-center">
                            <div className="items-center gap-2 flex">
                                <input 
                                    type="radio"
                                    id="yesOption" 
                                    value="yes" 
                                    checked={formData.isCashAtClosingVerified === 'yes'} 
                                    onChange={(e) => handleRadioChange('isCashAtClosingVerified', e.target.value)}
                                />
                                <label className="text-black text-opacity-60 font-semibold font-['Inter']">Yes</label>
                            </div>
                            <div className="items-center gap-2 flex">
                                <input 
                                    type="radio" 
                                    id="noOption"
                                    value="no" 
                                    checked={formData.isCashAtClosingVerified === 'no'} 
                                    onChange={(e) => handleRadioChange('isCashAtClosingVerified', e.target.value)}
                                />
                                <label className="text-black text-opacity-60 font-semibold font-['Inter']">No</label>
                            </div> 
                        </div>
                    </div>
                </div>
            </div>
            </div>
            
            <div className="flex w-full justify-center gap-10 md:gap-36 mt-2 bg-white">
                <button className="w-[100px] md:w-[150px] justify-center text-center text-shi-green bg-white border-white text-[16px] md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-slate-200 items-center md:items-start flex" onClick={handleBack}>
                    Back
                </button>
                <button className="w-[100px] md:w-[150px] justify-center items-center text-center text-white bg-shi-green text-[16px] md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-green-950 md:items-start flex" onClick={onNext}>
                    Next
                </button>
            </div>
        </MainCard>
        {isAssistVisible && (
                <div className="w-full md:w-auto absolute bottom-1 md:right-4">
                    <ShishitoAssist onClose={toggleAssist}/>
                </div>
            )}
        
        </div>
    )
}

export default PriceTerms; 